import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';

const TOLERANCE = 0.50; // pence/pence fence — warn but allow if diff ≤ this

function fmtDate(d) { return d ? formatDate(d) : '—'; }

// Always show pence — never round
function fmtAmt(v) {
  if (v == null) return '—';
  return '£' + Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MatchBadge({ diff }) {
  const abs = Math.abs(diff);
  if (abs < 0.005) return <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-emerald-100 text-emerald-700">Exact match</span>;
  if (abs <= TOLERANCE) return <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-amber-100 text-amber-700">Within {TOLERANCE * 100}p</span>;
  return <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-red-100 text-red-700">Mismatch: {diff > 0 ? '+' : ''}{fmtAmt(diff)}</span>;
}

function ReconciliationEvent({ event, onReverted }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [reverting, setReverting] = useState(false);

  const handleRevert = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Revert this reconciliation?\n\n${event.milestone_count} milestone${event.milestone_count !== 1 ? 's' : ''} will be returned to "Invoiced" status and their payment dates cleared.`)) return;
    setReverting(true);
    try {
      const res = await api.post(`remittance/reconciliations/${event.id}/revert`, {});
      toast.success(`Reconciliation reverted — ${event.milestone_count} milestone${event.milestone_count !== 1 ? 's' : ''} returned to Invoiced.`);
      // The reconciliation may have completed a SOW on the way in; reverting it
      // puts that SOW back to Active (or On Hold) rather than leaving it Complete.
      if (res?.reopened_sows) {
        toast.success(`${res.reopened_sows} SOW${res.reopened_sows !== 1 ? 's' : ''} reopened — no longer fully paid.`);
      }
      onReverted?.();
    } catch (err) {
      toast.error(err?.data?.error || 'Revert failed.');
    } finally {
      setReverting(false);
    }
  };

  const isReverted = !!event.reverted_at;

  return (
    <div className={`bg-white border rounded overflow-hidden ${isReverted ? 'border-gray-200 opacity-60' : 'border-gray-200'}`}>
      <div
        className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(x => !x)}
      >
        <span className="text-[8px] text-gray-400 select-none mt-0.5">{expanded ? '▾' : '▸'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold text-navy">{event.client_name}</span>
            <span className="text-[9px] font-extrabold text-emerald-700">{fmtAmt(event.payment_amount)}</span>
            <span className="text-[8px] text-gray-500">{fmtDate(event.payment_date)}</span>
            {event.reference && <span className="text-[8px] font-mono text-gray-400">{event.reference}</span>}
            {isReverted && (
              <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                Reverted{event.reverted_by_name ? ` by ${event.reverted_by_name}` : ''}
              </span>
            )}
          </div>
          <div className="text-[7px] text-gray-400 mt-0.5">
            {event.milestone_count} milestone{event.milestone_count !== 1 ? 's' : ''}
            {event.reconciled_by_name && <span> · Reconciled by {event.reconciled_by_name}</span>}
          </div>
        </div>
        {!isReverted && (
          <button
            onClick={handleRevert}
            disabled={reverting}
            className="flex-shrink-0 text-[7px] font-semibold px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-40 transition-colors"
          >
            {reverting ? 'Reverting…' : 'Revert'}
          </button>
        )}
      </div>
      {expanded && event.milestones?.length > 0 && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {event.milestones.map(m => (
            <div key={m.id} className="flex items-center gap-2 px-4 py-1.5 text-[8px]">
              <span className="font-mono text-gray-500">{m.sow_reference}</span>
              <span className="font-bold text-navy">{m.milestone_code}</span>
              {m.invoice_number && <span className="font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex-shrink-0">Inv {m.invoice_number}</span>}
              {m.description && <span className="text-gray-500 truncate flex-1">{m.description}</span>}
              <span className="font-semibold text-gray-700 flex-shrink-0">{fmtAmt(m.invoice_amount_incl ?? m.invoice_amount_excl ?? 0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RemittancePage() {
  const { toast } = useToast();

  // Step 1 state
  const [clients,      setClients]      = useState([]);
  const [clientId,     setClientId]     = useState('');
  const [paymentAmt,   setPaymentAmt]   = useState('');
  const [paymentDate,  setPaymentDate]  = useState(new Date().toISOString().split('T')[0]);
  const [reference,    setReference]    = useState('');
  const [loadingClients, setLoadingClients] = useState(true);

  // Step 2 state
  const [milestones,   setMilestones]   = useState([]);
  const [loadingMs,    setLoadingMs]    = useState(false);
  const [selected,     setSelected]     = useState(new Set());
  const [invoiceFilter, setInvoiceFilter] = useState('');

  // Reconcile state
  const [reconciling,  setReconciling]  = useState(false);
  const [reconciled,   setReconciled]   = useState(null); // { count, date }

  // History
  const [history,      setHistory]      = useState([]);
  const [loadingHist,  setLoadingHist]  = useState(true);

  const loadHistory = (cid) => {
    setLoadingHist(true);
    const qs = cid ? `?client_id=${cid}` : '';
    api.get(`remittance/history${qs}`)
      .then(d => setHistory(d || []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHist(false));
  };

  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    api.get('remittance/clients')
      .then(d => setClients(d || []))
      .catch(() => {})
      .finally(() => setLoadingClients(false));
  }, []);

  useEffect(() => {
    if (!clientId) { setMilestones([]); setSelected(new Set()); return; }
    setLoadingMs(true);
    setSelected(new Set());
    api.get(`remittance/milestones?client_id=${clientId}`)
      .then(d => setMilestones(d || []))
      .catch(() => setMilestones([]))
      .finally(() => setLoadingMs(false));
  }, [clientId]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const filteredMilestones = invoiceFilter.trim()
    ? milestones.filter(m => (m.invoice_number || '').toLowerCase().includes(invoiceFilter.trim().toLowerCase()))
    : milestones;

  const toggleAll = () => {
    if (selected.size === filteredMilestones.length) setSelected(new Set());
    else setSelected(new Set(filteredMilestones.map(m => m.id)));
  };

  const selectedMs   = milestones.filter(m => selected.has(m.id));
  const selectedSum  = Math.round(selectedMs.reduce((s, m) => s + Number(m.invoice_amount_incl ?? m.invoice_amount_excl ?? 0), 0) * 100) / 100;
  const payAmt       = Math.round((parseFloat(paymentAmt) || 0) * 100) / 100;
  const diff         = Math.round((selectedSum - payAmt) * 100) / 100;
  const absDiff      = Math.abs(diff);
  const canReconcile = selected.size > 0 && payAmt > 0 && absDiff <= TOLERANCE;
  const hasWarn      = selected.size > 0 && payAmt > 0 && absDiff >= 0.005 && absDiff <= TOLERANCE;
  const hasMismatch  = selected.size > 0 && payAmt > 0 && absDiff > TOLERANCE;

  // Auto-select milestones — exact-match search (1→2→3→4 combos), then greedy fallback
  const autoMatch = () => {
    if (!payAmt || milestones.length === 0) return;
    const target = payAmt;
    const items  = milestones.map(m => ({
      id:  m.id,
      amt: Math.round(Number(m.invoice_amount_incl ?? m.invoice_amount_excl ?? 0) * 100) / 100,
    }));
    const hit = (sum) => Math.abs(sum - target) <= TOLERANCE;

    // 1 milestone
    for (const a of items) {
      if (hit(a.amt)) { setSelected(new Set([a.id])); return; }
    }
    // 2 milestones
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        if (hit(items[i].amt + items[j].amt)) { setSelected(new Set([items[i].id, items[j].id])); return; }
      }
    }
    // 3 milestones
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        for (let k = j + 1; k < items.length; k++) {
          if (hit(items[i].amt + items[j].amt + items[k].amt)) {
            setSelected(new Set([items[i].id, items[j].id, items[k].id])); return;
          }
        }
      }
    }
    // 4 milestones (skip if very large list to keep it snappy)
    if (items.length <= 40) {
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          for (let k = j + 1; k < items.length; k++) {
            for (let l = k + 1; l < items.length; l++) {
              if (hit(items[i].amt + items[j].amt + items[k].amt + items[l].amt)) {
                setSelected(new Set([items[i].id, items[j].id, items[k].id, items[l].id])); return;
              }
            }
          }
        }
      }
    }
    // Greedy fallback: sort oldest-first, accumulate until close enough
    const sorted = [...items].sort((a, b) => {
      const ma = milestones.find(m => m.id === a.id);
      const mb = milestones.find(m => m.id === b.id);
      return (ma?.est_pay_date || '').localeCompare(mb?.est_pay_date || '');
    });
    let running = 0;
    const picks = new Set();
    for (const m of sorted) {
      if (running + m.amt <= target + TOLERANCE) {
        picks.add(m.id);
        running = Math.round((running + m.amt) * 100) / 100;
        if (hit(running)) break;
      }
    }
    setSelected(picks);
  };

  const reconcile = async () => {
    if (selected.size === 0 || payAmt <= 0) return;
    const ok = hasWarn
      ? window.confirm(`The selected total (${fmtAmt(selectedSum)}) differs from the payment (${fmtAmt(payAmt)}) by ${fmtAmt(absDiff)}. Proceed anyway?`)
      : true;
    if (!ok) return;
    setReconciling(true);
    try {
      const res = await api.post('remittance/reconcile', {
        milestone_ids: [...selected],
        paid_date: paymentDate,
        payment_amount: payAmt,
        reference: reference || undefined,
        notes: reference ? `Remittance ref: ${reference}` : undefined,
      });
      setReconciled({ count: res.updated, date: paymentDate });
      toast.success(`${res.updated} milestone${res.updated !== 1 ? 's' : ''} marked as Paid.`);
      // Any SOW this payment finished off closes itself — say which, so it isn't
      // a surprise that they have left the Active tab on the tracker.
      const done = res.completed_sows || [];
      if (done.length) {
        toast.success(done.length === 1
          ? `${done[0].reference || 'SOW'} marked Complete — every milestone on it is now paid.`
          : `${done.length} SOWs marked Complete — every milestone on them is now paid.`);
      }
      // Refresh milestones list and history
      setSelected(new Set());
      setPaymentAmt('');
      setReference('');
      loadHistory(clientId || undefined);
      if (clientId) {
        setLoadingMs(true);
        api.get(`remittance/milestones?client_id=${clientId}`)
          .then(d => setMilestones(d || []))
          .catch(() => {})
          .finally(() => setLoadingMs(false));
      }
    } catch (e) {
      toast.error(e?.data?.error || 'Reconciliation failed.');
    } finally {
      setReconciling(false);
    }
  };

  const clientName = clients.find(c => c.id === clientId)?.name || '';

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-base font-extrabold text-navy">Payment Reconciliation</h2>
        <div className="text-[9px] text-gray-500">Enter a received payment and match it to outstanding invoiced milestones.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* ── LEFT: Payment entry ── */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white border border-gray-200 rounded p-3 space-y-3">
            <div className="text-[10px] font-bold text-gray-700 uppercase tracking-wide">Payment Details</div>

            <div>
              <label className="form-label">Client *</label>
              {loadingClients
                ? <div className="text-[9px] text-gray-400">Loading…</div>
                : <select className="form-select" value={clientId} onChange={e => { setClientId(e.target.value); setReconciled(null); }}>
                    <option value="">— select client —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
              }
            </div>

            <div>
              <label className="form-label">Amount Received (inc. VAT) *</label>
              <div className="flex items-stretch form-input p-0 overflow-hidden">
                <span className="flex items-center px-2 text-[10px] text-gray-400 border-r border-gray-200 select-none bg-gray-50">£</span>
                <input
                  type="number" step="0.01" min="0"
                  className="flex-1 px-2 py-[4px] text-[10px] bg-transparent outline-none"
                  placeholder="0.00"
                  value={paymentAmt}
                  onChange={e => { setPaymentAmt(e.target.value); setReconciled(null); }}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Payment Date *</label>
              <input type="date" className="form-input" value={paymentDate}
                onChange={e => setPaymentDate(e.target.value)} />
            </div>

            <div>
              <label className="form-label">Reference / Notes</label>
              <input type="text" className="form-input" placeholder="e.g. BACS ref, remittance advice no."
                value={reference} onChange={e => setReference(e.target.value)} />
            </div>

            {clientId && payAmt > 0 && milestones.length > 0 && (
              <button onClick={autoMatch}
                className="w-full text-[9px] font-semibold py-1.5 border border-navy text-navy rounded hover:bg-navy hover:text-white transition-colors">
                ✦ Auto-match milestones
              </button>
            )}
          </div>

          {/* Running total / match status */}
          {selected.size > 0 && payAmt > 0 && (
            <div className={`rounded p-3 border text-[9px] space-y-1.5 ${hasMismatch ? 'bg-red-50 border-red-200' : hasWarn ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-700">Payment received</span>
                <span className="font-extrabold text-gray-900">{fmtAmt(payAmt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-700">Selected milestones ({selected.size})</span>
                <span className="font-extrabold text-gray-900">{fmtAmt(selectedSum)}</span>
              </div>
              <div className="border-t border-gray-200 pt-1.5 flex items-center justify-between">
                <span className="font-bold text-gray-700">Difference</span>
                <MatchBadge diff={diff} />
              </div>
            </div>
          )}

          {/* Reconcile button */}
          {selected.size > 0 && payAmt > 0 && (canReconcile || hasWarn) && (
            <button
              onClick={reconcile}
              disabled={reconciling}
              className="w-full py-2 text-[10px] font-extrabold bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 transition-colors">
              {reconciling ? 'Reconciling...' : `Confirm Payment - Mark ${selected.size} Milestone${selected.size !== 1 ? 's' : ''} as Paid`}
            </button>
          )}
          {hasMismatch && (
            <div className="text-[8px] text-red-600 font-semibold text-center">
              Difference exceeds {TOLERANCE * 100}p — adjust selection before reconciling.
            </div>
          )}

          {reconciled && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-[9px] text-emerald-700 font-semibold text-center">
              ✓ {reconciled.count} milestone{reconciled.count !== 1 ? 's' : ''} marked Paid on {fmtDate(reconciled.date)}
            </div>
          )}
        </div>

        {/* ── RIGHT: Milestone list ── */}
        <div className="lg:col-span-3">
          {!clientId && (
            <div className="bg-white border border-dashed border-gray-200 rounded p-8 text-center text-[9px] text-gray-400">
              Select a client to see outstanding invoiced milestones
            </div>
          )}

          {clientId && loadingMs && (
            <div className="text-center py-8 text-gray-400 text-sm">Loading milestones…</div>
          )}

          {clientId && !loadingMs && milestones.length === 0 && (
            <div className="bg-white border border-dashed border-gray-200 rounded p-8 text-center text-[9px] text-gray-400">
              No outstanding invoiced milestones for {clientName}
            </div>
          )}

          {clientId && !loadingMs && milestones.length > 0 && (
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              {/* Table header */}
              <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
                <input type="checkbox"
                  checked={filteredMilestones.length > 0 && selected.size === filteredMilestones.length}
                  onChange={toggleAll}
                  className="accent-navy flex-shrink-0" />
                <span className="text-[9px] font-bold text-gray-600 flex-1">
                  {filteredMilestones.length}{filteredMilestones.length !== milestones.length ? ` of ${milestones.length}` : ''} invoiced milestone{milestones.length !== 1 ? 's' : ''} — {clientName}
                </span>
                <span className="text-[9px] text-gray-400">
                  Total: <strong>{fmtAmt(milestones.reduce((s, m) => s + Number(m.invoice_amount_incl ?? m.invoice_amount_excl ?? 0), 0))}</strong>
                </span>
              </div>
              {/* Invoice number search */}
              <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                <input
                  type="text"
                  value={invoiceFilter}
                  onChange={e => setInvoiceFilter(e.target.value)}
                  placeholder="Filter by invoice number…"
                  className="w-full text-[9px] px-2 py-1 border border-gray-200 rounded bg-white outline-none focus:border-navy"
                />
              </div>

              <div className="divide-y divide-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
                {filteredMilestones.length === 0 && (
                  <div className="px-4 py-6 text-center text-[9px] text-gray-400">No milestones match "{invoiceFilter}"</div>
                )}
                {filteredMilestones.map(m => {
                  const amt = m.invoice_amount_incl ?? m.invoice_amount_excl ?? 0;
                  const isSelected = selected.has(m.id);
                  const overdue = m.est_pay_date && m.est_pay_date < new Date().toISOString().split('T')[0];
                  return (
                    <div key={m.id}
                      onClick={() => toggle(m.id)}
                      className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggle(m.id)}
                        onClick={e => e.stopPropagation()}
                        className="mt-0.5 accent-navy flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[9px] font-bold text-navy">{m.reference} · {m.milestone_code}</span>
                          {m.invoice_number && (
                            <span className="text-[7px] font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-600">Inv {m.invoice_number}</span>
                          )}
                          {overdue && (
                            <span className="text-[7px] font-semibold px-1 py-0.5 rounded bg-red-100 text-red-600">Overdue</span>
                          )}
                        </div>
                        <div className="text-[8px] text-gray-500 mt-0.5 truncate">{m.description || m.sow_detail || '—'}</div>
                        <div className="text-[7px] text-gray-400 mt-0.5 flex gap-2 flex-wrap">
                          {m.formal_invoice_date && <span>Invoiced {fmtDate(m.formal_invoice_date)}</span>}
                          {m.est_pay_date && <span className={overdue ? 'text-red-500 font-semibold' : ''}>Due {fmtDate(m.est_pay_date)}</span>}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className={`text-[10px] font-extrabold ${isSelected ? 'text-navy' : 'text-gray-700'}`}>{fmtAmt(amt)}</div>
                        <div className="text-[7px] text-gray-400">inc. VAT</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.size > 0 && (
                <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                  <span className="text-[8px] text-gray-500">{selected.size} selected</span>
                  <span className="text-[9px] font-extrabold text-navy">{fmtAmt(selectedSum)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Previous Reconciliations ── */}
      <div className="mt-6">
        <h3 className="text-[10px] font-bold text-navy uppercase tracking-wide mb-2">Previous Reconciliations</h3>
        {loadingHist ? (
          <div className="text-[9px] text-gray-400 p-3">Loading history...</div>
        ) : history.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded text-[9px] text-gray-400 p-4">No reconciliations recorded yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map(event => (
              <ReconciliationEvent key={event.id} event={event} onReverted={() => {
                loadHistory(clientId || undefined);
                if (clientId) {
                  setLoadingMs(true);
                  api.get(`remittance/milestones?client_id=${clientId}`)
                    .then(d => setMilestones(d || []))
                    .catch(() => {})
                    .finally(() => setLoadingMs(false));
                }
              }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
